// src/pages/Destinos.tsx
import React, { useState, useMemo } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useAllDestinations, useRegionsAndCountries } from '../hooks/useCMS';
import { Destination } from '../types/cms';
import SafeImage from '../components/SafeImage';
import CountryFlag from '../components/CountryFlag';
import SEOHead from '../components/SEOHead';

function Destinos() {
  const { data: destinationsData, loading: destinationsLoading, error: destinationsError } = useAllDestinations();
  const { data: regionsData, loading: regionsLoading, error: regionsError } = useRegionsAndCountries();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'regions'>('grid');

  const destinations = destinationsData?.destinations || [];
  const regions = regionsData?.regions || [];

  // Filtrar destinos
  const filteredDestinations = useMemo(() => {
    return destinations.filter(dest => {
      const matchesSearch = searchTerm === '' || 
        dest.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        dest.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        dest.country?.name.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesRegion = selectedRegion === '' ||
        dest.country?.region?.name === selectedRegion;

      const matchesCountry = selectedCountry === '' ||
        dest.country?.name === selectedCountry;

      return matchesSearch && matchesRegion && matchesCountry;
    });
  }, [destinations, searchTerm, selectedRegion, selectedCountry]);

  // Obtener países únicos para el filtro
  const countries = useMemo(() => {
    const countrySet = new Set<string>();
    destinations.forEach(dest => {
      if (dest.country?.name) {
        countrySet.add(dest.country.name);
      }
    });
    return Array.from(countrySet).sort();
  }, [destinations]);

  const handleRegionChange = (event: SelectChangeEvent) => {
    setSelectedRegion(event.target.value);
    setSelectedCountry(''); // Reset country when region changes
  };

  const handleCountryChange = (event: SelectChangeEvent) => {
    setSelectedCountry(event.target.value);
  };

  if (destinationsLoading || regionsLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (destinationsError || regionsError) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">
          Error al cargar los destinos. Por favor, intenta de nuevo más tarde.
        </Alert>
      </Container>
    );
  }

  const renderDestination = (dest: Destination) => (
    <Card key={dest.id} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <SafeImage
        src={dest.thumbnail?.publicUrl}
        alt={dest.title}
        height={200}
        sx={{ 
          width: '100%',
          objectFit: 'cover'
        }}
      />
      <CardContent sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <CountryFlag
            src={dest.country?.bandera?.publicUrl}
            alt={dest.country?.name || 'Bandera del país'}
            width={24}
            height={16}
            sx={{ mr: 1 }}
          />
          <Typography variant="body2" color="text.secondary">
            {dest.country?.name}
          </Typography>
        </Box>
        
        <Typography gutterBottom variant="h6" component="h2">
          {dest.title}
        </Typography>
        
        {dest.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {dest.description}
          </Typography>
        )}

        {/* Paquetes relacionados */}
        {dest.packages && dest.packages.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {dest.packages.length} paquete{dest.packages.length !== 1 ? 's' : ''} disponible{dest.packages.length !== 1 ? 's' : ''}
            </Typography>
            <Typography variant="body2" color="primary">
              Desde ${Math.min(...dest.packages.map(p => p.priceFrom || 0).filter(p => p > 0)).toLocaleString().replace(/,/g, '.')}
            </Typography>
          </Box>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 'auto', gap: 1 }}>
          {dest.packages && dest.packages.length > 0 && (
            <Button 
              variant="outlined" 
              color="primary"
              href={`/paquetes?destino=${dest.title}`}
            >
              Ver paquetes
            </Button>
          )}
          <Button 
            variant="contained" 
            color="primary"
            href={`/destinos/${dest.slug}`}
          >
            Ver destino
          </Button>
        </Box>
      </CardContent>
    </Card>
  );

  const renderRegionView = () => (
    <Box>
      {regions.map((region) => (
        <Accordion key={region.id} defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">{region.name}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              {region.countries?.map((country) => (
                <Grid item xs={12} sm={6} md={4} key={country.id}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <CountryFlag
                          src={country.bandera?.publicUrl}
                          alt={country.name}
                          width={32}
                          height={20}
                          sx={{ mr: 1.5 }}
                        />
                        <Typography variant="h6">{country.name}</Typography>
                      </Box>
                      
                      {country.destinations && country.destinations.length > 0 ? (
                        <Box>
                          {country.destinations.map((dest) => (
                            <Button
                              key={dest.id}
                              variant="text"
                              size="small"
                              href={`/destinos/${dest.slug}`}
                              sx={{ display: 'block', textAlign: 'left', mb: 1 }}
                            >
                              {dest.title}
                            </Button>
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No hay destinos disponibles
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );

  return (
    <>
      <SEOHead
        title="Destinos de Viaje desde Uruguay"
        description="Explorá los mejores destinos turísticos de Argentina, Brasil y Latinoamérica. Guías completas, tips y paquetes disponibles para cada destino."
        keywords={[
          'destinos turísticos',
          'viajes a Argentina',
          'viajes a Brasil',
          'guías de viaje',
          'destinos desde Uruguay',
          'turismo',
        ]}
        url="/destinos"
        type="website"
      />
      <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Typography variant="h3" component="h1" gutterBottom>
        Destinos
      </Typography>
      
      {/* Filtros */}
      <Box sx={{ mb: 4, p: 3, bgcolor: 'grey.50', borderRadius: 2 }}>
        {/* <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Filtros
          </Typography>
          <Box>
            <Button
              variant={viewMode === 'grid' ? 'contained' : 'outlined'}
              onClick={() => setViewMode('grid')}
              sx={{ mr: 1 }}
            >
              Vista de cuadrícula
            </Button>
            <Button
              variant={viewMode === 'regions' ? 'contained' : 'outlined'}
              onClick={() => setViewMode('regions')}
            >
              Vista por regiones
            </Button>
          </Box>
        </Box> */}
        
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Buscar destinos"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Región</InputLabel>
              <Select
                value={selectedRegion}
                label="Región"
                onChange={handleRegionChange}
              >
                <MenuItem value="">Todas las regiones</MenuItem>
                {regions.map((region) => (
                  <MenuItem key={region.id} value={region.name}>
                    {region.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>País</InputLabel>
              <Select
                value={selectedCountry}
                label="País"
                onChange={handleCountryChange}
              >
                <MenuItem value="">Todos los países</MenuItem>
                {countries.map((country) => (
                  <MenuItem key={country} value={country}>
                    {country}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>

      {/* Resultados */}
      {viewMode === 'grid' && (
        <>
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" color="text.secondary">
              {filteredDestinations.length} destino{filteredDestinations.length !== 1 ? 's' : ''} encontrado{filteredDestinations.length !== 1 ? 's' : ''}
            </Typography>
          </Box>

          {/* Lista de destinos */}
          {filteredDestinations.length > 0 ? (
            <Grid container spacing={3}>
              {filteredDestinations.map((dest) => (
                <Grid item xs={12} sm={6} md={4} key={dest.id}>
                  {renderDestination(dest)}
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box textAlign="center" py={8}>
              <Typography variant="h5" color="text.secondary">
                No se encontraron destinos con los filtros seleccionados.
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                Intenta ajustar los filtros de búsqueda.
              </Typography>
            </Box>
          )}
        </>
      )}

      {/* Vista por regiones */}
      {viewMode === 'regions' && renderRegionView()}
    </Container>
    </>
  );
}

export default Destinos;
